/**
 * DocumentQaUI
 *
 * Step 1 – Picker: 3/4 document list (10 visible) + 1/4 description panel.
 * Step 2 – Chat:   3/4 ChatToolUI + 1/4 enriched artifact panel.
 *                  File upload supported in chat input.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiFetch } from '@/lib/apiFetch';
import { MultiSelectDropdown } from '@/components/tools/MultiSelectDropdown';
import { ChatToolUI } from './ChatToolUI';
import { DocumentViewerModal } from './DocumentViewerModal';
import {
  Search,
  FileText,
  FileSpreadsheet,
  FileCode2,
  FileAudio,
  FileVideo,
  FileImage,
  File,
  Globe,
  X,
  ChevronRight,
  ExternalLink,
  Calendar,
  Tag,
  Paperclip,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react';
import type { Tool } from '@/types/database';

// ── Types ────────────────────────────────────────────────────────────────────

interface Artifact {
  id: string;
  url: string;
  mime_type?: string;
  kind?: string;
  first_seen_at?: string;
  meeting_date?: string;
  department?: string;
  meeting_type?: string;
  byte_size?: number;
  /** Populated by picker-search: trimmed text of the best-matching chunk */
  snippet?: string;
  /** Populated by picker-search: count of matching chunks in this document */
  matching_chunks?: number;
}

interface Props {
  tool: Tool;
  /** Called when the user clicks back from the picker (to leave the tool entirely) */
  onBack?: () => void;
  /** Workspace name to display in the chat header */
  workspaceName?: string;
  /** Workspace ID — used for breadcrumb navigation link */
  workspaceId?: string;
  /**
   * When provided, skip the picker and jump straight to the chat view with
   * this artifact pre-selected. Passed via router location state from
   * Full Text Search's "Analyze in Document Q&A" button.
   */
  preselectedArtifact?: Artifact;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function labelFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    const seg = path.split('/').filter(Boolean).pop() ?? url;
    return decodeURIComponent(seg)
      .replace(/\.[^.]+$/, '')
      .replace(/[-_]/g, ' ');
  } catch {
    return url;
  }
}

function formatBytes(n?: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Per-kind icon with appropriate colour */
function docTypeIcon(
  kind?: string,
  mime?: string,
  cls = 'h-4 w-4 flex-shrink-0'
): React.ReactElement {
  const k = kind ?? '';
  const m = mime ?? '';
  if (k === 'pdf' || m.includes('pdf'))
    return <FileText className={`${cls} text-red-500`} />;
  if (k === 'docx' || k === 'doc' || m.includes('word') || m.includes('msword'))
    return <FileText className={`${cls} text-blue-600`} />;
  if (
    k === 'xlsx' ||
    k === 'xls' ||
    k === 'csv' ||
    m.includes('spreadsheet') ||
    m.includes('excel')
  )
    return <FileSpreadsheet className={`${cls} text-green-600`} />;
  if (k === 'html_snapshot' || m.includes('html'))
    return <Globe className={`${cls} text-indigo-500`} />;
  if (k === 'audio' || m.includes('audio'))
    return <FileAudio className={`${cls} text-amber-500`} />;
  if (k === 'video' || m.includes('video'))
    return <FileVideo className={`${cls} text-purple-500`} />;
  if (k === 'image' || m.includes('image'))
    return <FileImage className={`${cls} text-pink-500`} />;
  if (m.includes('xml') || m.includes('code'))
    return <FileCode2 className={`${cls} text-teal-500`} />;
  return <File className={`${cls} text-gray-400`} />;
}

/**
 * Returns the best available document date label + value.
 * Prefers meeting_date (the document's own date) over first_seen_at (scrape date).
 */
function docDate(a: Artifact): { label: string; value: string } | null {
  if (a.meeting_date) {
    return {
      label: 'Document date',
      value: new Date(a.meeting_date).toLocaleDateString(undefined, {
        dateStyle: 'medium',
      }),
    };
  }
  if (a.first_seen_at) {
    return {
      label: 'First seen',
      value: new Date(a.first_seen_at).toLocaleDateString(undefined, {
        dateStyle: 'medium',
      }),
    };
  }
  return null;
}

// ── Component ────────────────────────────────────────────────────────────────

export const DocumentQaUI: React.FC<Props> = ({
  tool,
  onBack,
  workspaceName,
  workspaceId,
  preselectedArtifact,
}) => {
  const navigate = useNavigate();

  // Picker state — initialise from preselectedArtifact when provided
  const [query, setQuery] = useState('');
  const [filterClasses, setFilterClasses] = useState<string[]>([]);
  const [filterDepts, setFilterDepts] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  // Departments + classifications derived from the filter-options endpoint
  // (distinct across the whole archive — not a limited sample)
  const [allDepts, setAllDepts] = useState<string[]>([]);
  const [allClasses, setAllClasses] = useState<string[]>([]);
  const [selected, setSelected] = useState<Artifact[]>(
    preselectedArtifact ? [preselectedArtifact] : []
  );
  const [focused, setFocused] = useState<Artifact | null>(null);
  const [loading, setLoading] = useState(false);
  // Jump straight to chat if a document was pre-selected (e.g. from Full Text Search)
  const [ready, setReady] = useState(!!preselectedArtifact);
  // Artifact currently open in the "View document" modal (PDF preview + download)
  const [viewerArtifact, setViewerArtifact] = useState<Artifact | null>(null);

  // Upload state (file upload is owned here; passed down to ChatToolUI via props)
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sessionArtifacts, setSessionArtifacts] = useState<
    { id: string; filename: string }[]
  >([]);
  // Stable session ID for this Document Q&A session — ties uploaded chunks together
  const sessionId = useRef<string>(crypto.randomUUID());

  // ── Artifact search ────────────────────────────────────────────────────────
  // When a query string is present: hit /api/artifacts/picker-search (FTS on
  // chunks, scoped to workspace jurisdictions, ranked by best-matching chunk).
  // When empty: browse /api/artifacts (unranked metadata list).

  const search = useCallback(
    async (
      q: string,
      classes: string[],
      depts: string[],
      from: string,
      to: string
    ) => {
      setLoading(true);
      try {
        if (q.trim()) {
          // ── Content search (picker-search) ──────────────────────────────────
          const params = new URLSearchParams({ q: q.trim() });
          if (workspaceId) params.set('workspace_id', workspaceId);
          if (classes.length > 0) params.set('meeting_type', classes.join(','));
          if (depts.length > 0) params.set('department', depts.join(','));
          if (from) params.set('meeting_date_min', from);
          if (to) params.set('meeting_date_max', to);

          const url = `/api/artifacts/picker-search?${params.toString()}`;
          const res = await apiFetch(url);
          if (!res.ok) {
            setArtifacts([]);
            return;
          }
          const data = await res.json();
          const rows: Artifact[] = (data?.data ?? []).map(
            (r: {
              artifact: Artifact;
              snippet?: string;
              matching_chunks?: number;
            }) => ({
              ...r.artifact,
              snippet: r.snippet,
              matching_chunks: r.matching_chunks,
            })
          );
          setArtifacts(rows);
        } else {
          // ── Browse (no query) ───────────────────────────────────────────────
          const params = new URLSearchParams({ limit: '200' });
          if (classes.length > 0) params.set('meeting_type', classes.join(','));
          if (depts.length > 0) params.set('department', depts.join(','));
          if (from) params.set('meeting_date_min', from);
          if (to) params.set('meeting_date_max', to);
          if (workspaceId) params.set('workspace_id', workspaceId);

          const res = await apiFetch(`/api/artifacts?${params.toString()}`);
          if (!res.ok) {
            setArtifacts([]);
            return;
          }
          const data = await res.json();
          const raw: Artifact[] = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];
          setArtifacts(raw);
        }
      } catch {
        setArtifacts([]);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    // Load distinct departments + classifications once for the dropdowns
    apiFetch('/api/artifacts/filter-options')
      .then((r) => r.json())
      .then((data) => {
        const opts = data?.data ?? data ?? {};
        const depts = (opts.departments ?? []) as string[];
        const classes = (opts.classifications ?? []) as string[];
        setAllDepts(depts);
        setAllClasses(classes);
      })
      .catch(() => {});
    void search('', [], [], '', '');
  }, [search]);

  useEffect(() => {
    const t = setTimeout(
      () => void search(query, filterClasses, filterDepts, dateFrom, dateTo),
      300
    );
    return () => clearTimeout(t);
  }, [query, filterClasses, filterDepts, dateFrom, dateTo, search]);

  // ── Picker helpers ─────────────────────────────────────────────────────────

  const MAX_SELECTED = 10;

  const toggle = (artifact: Artifact) => {
    setSelected((prev) => {
      if (prev.find((a) => a.id === artifact.id)) {
        return prev.filter((a) => a.id !== artifact.id);
      }
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, artifact];
    });
  };
  const isSelected = (id: string) => selected.some((a) => a.id === id);

  // ── File upload handler (passed to ChatToolUI) ─────────────────────────────
  const handleFileUpload = async (file: File) => {
    setUploadError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId.current);

      const res = await apiFetch('/api/artifacts/upload-session', {
        method: 'POST',
        body: formData,
        headers: {},
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }));
        setUploadError(err.error ?? 'Upload failed');
        return;
      }

      const data = await res.json();
      setSessionArtifacts((prev) => [
        ...prev,
        { id: data.artifact_id, filename: file.name },
      ]);
    } catch {
      setUploadError('Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  // ── Shared header — rendered in both picker and chat views ──────────────

  const header = (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/tools')}
          className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
        >
          Tools
        </button>
        {workspaceId && (
          <>
            <span className="text-gray-900 text-sm">/</span>
            <button
              type="button"
              onClick={() => navigate(`/workspaces/${workspaceId}`)}
              className="text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
            >
              {workspaceName ?? 'Workspace'}
            </button>
          </>
        )}
      </div>

      {/* Page title */}
      <h1 className="text-3xl font-bold text-gray-900 flex items-baseline gap-2 flex-wrap">
        {tool.name}
        {workspaceName && (
          <span className="text-xl font-semibold text-gray-900">
            ({workspaceName})
          </span>
        )}
      </h1>
      {tool.description && (
        <p className="mt-1 text-gray-900">
          Select one or more documents to scope your session. Attach additional
          files at any point to extend the conversation.{' '}
          <span className="font-medium text-gray-900">
            Up to 10 documents may be selected.
          </span>
        </p>
      )}
    </div>
  );

  // ── PICKER VIEW ───────────────────────────────────────────────────────────

  if (!ready) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {/* Force light theme for the picker content — overrides global dark CSS vars */}
        <div
          className="flex gap-4 h-[calc(100vh-290px)] min-h-[460px]"
          style={
            {
              '--background': '0 0% 100%',
              '--foreground': '222 47% 11%',
              '--popover': '0 0% 100%',
              '--popover-foreground': '222 47% 11%',
              '--card': '0 0% 100%',
              '--card-foreground': '222 47% 11%',
              '--border': '214 32% 91%',
              '--input': '0 0% 100%',
              '--muted': '210 40% 96%',
              '--muted-foreground': '215 16% 47%',
              '--accent': '210 40% 96%',
              '--accent-foreground': '222 47% 11%',
              '--secondary': '210 40% 96%',
              '--secondary-foreground': '222 47% 11%',
              color: '#111827',
            } as React.CSSProperties
          }
        >
          {/* 3/4 document list */}
          <div className="flex flex-col flex-[3] min-w-0">
            {/* Search + filters */}
            <div className="space-y-2 mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search documents…"
                  className="pl-9 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400 focus-visible:ring-blue-500"
                />
              </div>

              {/* Filter row */}
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <MultiSelectDropdown
                  placeholder="All classifications"
                  options={allClasses}
                  selected={filterClasses}
                  onChange={setFilterClasses}
                  className="w-40"
                />

                <MultiSelectDropdown
                  placeholder="All departments"
                  options={allDepts}
                  selected={filterDepts}
                  onChange={setFilterDepts}
                  className="flex-1"
                />

                {/* Date range — optional, blank = no bound */}
                <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  aria-label="From date"
                  title="From date"
                  className="h-8 text-xs w-36 bg-white text-gray-900 border-gray-300 focus-visible:ring-blue-500"
                />
                <span className="text-xs text-gray-400">–</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  aria-label="To date"
                  title="To date"
                  className="h-8 text-xs w-36 bg-white text-gray-900 border-gray-300 focus-visible:ring-blue-500"
                />

                {(filterClasses.length > 0 ||
                  filterDepts.length > 0 ||
                  dateFrom ||
                  dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-gray-900 hover:text-gray-600 px-2"
                    onClick={() => {
                      setFilterClasses([]);
                      setFilterDepts([]);
                      setDateFrom('');
                      setDateTo('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* List — all items, 10-row height, scrollable */}
            <div
              className="flex-1 border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-y-auto bg-white"
              style={{ maxHeight: '27.5rem' }}
            >
              {loading && (
                <div className="py-6 text-center text-sm text-gray-900">
                  Searching…
                </div>
              )}
              {!loading && artifacts.length === 0 && (
                <div className="py-6 text-center text-sm text-gray-900">
                  No documents found.
                </div>
              )}
              {!loading &&
                artifacts.map((a) => {
                  const atCap =
                    selected.length >= MAX_SELECTED && !isSelected(a.id);
                  return (
                    <button
                      key={a.id}
                      disabled={atCap}
                      onClick={() => {
                        const wasSelected = isSelected(a.id);
                        toggle(a);
                        if (wasSelected) {
                          setFocused((prev) =>
                            prev?.id === a.id ? null : prev
                          );
                        } else {
                          setFocused(a);
                        }
                      }}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                        atCap
                          ? 'opacity-40 cursor-not-allowed'
                          : isSelected(a.id)
                            ? 'bg-blue-50 hover:bg-blue-100'
                            : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Checkbox */}
                      <div
                        className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                          isSelected(a.id)
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected(a.id) && (
                          <svg
                            className="h-2.5 w-2.5 text-white"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1 4l3 3 5-6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      {docTypeIcon(a.kind, a.mime_type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {labelFromUrl(a.url)}
                        </p>
                        {(a.department ||
                          a.meeting_type ||
                          a.kind ||
                          a.meeting_date ||
                          a.first_seen_at) && (
                          <p className="text-xs text-gray-700 truncate">
                            {[
                              a.meeting_type ?? a.department ?? a.kind,
                              docDate(a)?.value,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        )}
                        {/* Content snippet from picker-search */}
                        {a.snippet && (
                          <p className="text-xs text-gray-900 italic mt-0.5 line-clamp-2">
                            matched on: &ldquo;{a.snippet}&rdquo;
                          </p>
                        )}
                        {/* Matching passages badge */}
                        {a.matching_chunks != null && a.matching_chunks > 1 && (
                          <span className="inline-block mt-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                            {a.matching_chunks} matching passages
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3">
              <div className="flex items-center gap-3">
                {onBack && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onBack}
                    className="border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900"
                  >
                    ← Back
                  </Button>
                )}
                <span className="text-sm text-gray-900">
                  {selected.length === 0
                    ? 'Select at least one document'
                    : selected.length >= MAX_SELECTED
                      ? `${selected.length}/${MAX_SELECTED} documents selected — limit reached`
                      : `${selected.length}/${MAX_SELECTED} document${selected.length > 1 ? 's' : ''} selected`}
                </span>
              </div>
              <Button
                disabled={selected.length === 0}
                onClick={() => setReady(true)}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
              >
                Start Q&amp;A
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Selection limit notice */}
            {selected.length >= MAX_SELECTED && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Maximum of {MAX_SELECTED} documents selected. Remove a document
                to add another.
              </p>
            )}
          </div>

          {/* 1/4 description panel */}
          <div className="flex flex-col flex-[1] min-w-[200px] max-w-xs border-l border-gray-200 pl-4 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-3">
              Document Details
            </p>

            {!focused && selected.length === 0 && (
              <p className="text-xs text-gray-900 italic">
                Select a document to see its details.
              </p>
            )}

            {/* Show focused item first, then remaining selected */}
            {[
              ...(focused ? [focused] : []),
              ...selected.filter((a) => a.id !== focused?.id),
            ].map((a, i) => (
              <div
                key={a.id}
                className={`relative rounded-xl border p-3 space-y-2 mb-3 ${
                  i === 0 && focused?.id === a.id
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                {/* Remove button */}
                <button
                  onClick={() => {
                    setSelected((prev) => prev.filter((s) => s.id !== a.id));
                    if (focused?.id === a.id) setFocused(null);
                  }}
                  className="absolute top-2 right-2 p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove document"
                >
                  <X className="h-3.5 w-3.5" />
                </button>

                <div className="flex items-start gap-2 pr-5">
                  {docTypeIcon(
                    a.kind,
                    a.mime_type,
                    'h-4 w-4 mt-0.5 flex-shrink-0'
                  )}
                  <p className="text-xs font-semibold text-gray-900 leading-snug break-words">
                    {labelFromUrl(a.url)}
                  </p>
                </div>
                {a.kind && (
                  <div className="flex items-center gap-1">
                    <Tag className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-700 capitalize">
                      {a.kind.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
                {docDate(a) && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-700">
                      {docDate(a)!.label}: {docDate(a)!.value}
                    </span>
                  </div>
                )}
                {a.byte_size != null && (
                  <p className="text-xs text-gray-900 pl-4">
                    {formatBytes(a.byte_size)}
                  </p>
                )}
                {(a.department || a.meeting_type) && (
                  <p className="text-xs text-gray-700 pl-4">
                    {[a.department, a.meeting_type].filter(Boolean).join(' · ')}
                  </p>
                )}
                <button
                  onClick={() => setViewerArtifact(a)}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  View document
                </button>
              </div>
            ))}
          </div>
        </div>

        {viewerArtifact && (
          <DocumentViewerModal
            artifactId={viewerArtifact.id}
            sourceUrl={viewerArtifact.url}
            title={labelFromUrl(viewerArtifact.url)}
            kind={viewerArtifact.kind}
            onClose={() => setViewerArtifact(null)}
          />
        )}
      </div>
    );
  }

  // ── CHAT VIEW ─────────────────────────────────────────────────────────────

  // Upload error banner passed to ChatToolUI as inputAddon
  const uploadAddon = uploadError ? (
    <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 text-xs text-red-700">
      <span className="flex-1">{uploadError}</span>
      <button onClick={() => setUploadError(null)}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      {header}

      <div className="flex gap-4 h-[calc(100vh-260px)] min-h-[520px]">
        {/* 3/4 Chat — fully handled by ChatToolUI (copy, thumbs, follow-ups included) */}
        <div className="flex flex-col flex-[3] min-w-0">
          {/* "Change documents" back button */}
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="outline"
              size="sm"
              className="text-xs border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900"
              onClick={() => setReady(false)}
            >
              ← Change documents
            </Button>
          </div>

          <ChatToolUI
            tool={tool}
            workspaceId={workspaceId}
            artifactIds={[
              ...selected.map((a) => a.id),
              ...sessionArtifacts.map((s) => s.id),
            ]}
            placeholder="Ask a question about the selected documents…"
            onFileUpload={handleFileUpload}
            uploading={uploading}
            inputAddon={uploadAddon}
            className="flex-1 !h-auto"
          />
        </div>
        {/* 1/4 Artifact panel */}
        <div className="flex flex-col flex-[1] min-w-[200px] max-w-xs border-l pl-4 overflow-y-auto">
          <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-3">
            Documents in Scope
          </p>

          <div className="space-y-4">
            {selected.map((a) => (
              <div
                key={a.id}
                className="relative rounded-xl border border-gray-200 bg-white p-3 space-y-2 shadow-sm"
              >
                {/* Remove button */}
                <button
                  onClick={() =>
                    setSelected((prev) => prev.filter((s) => s.id !== a.id))
                  }
                  className="absolute top-2 right-2 p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove document"
                >
                  <X className="h-3.5 w-3.5" />
                </button>

                {/* Title */}
                <div className="flex items-start gap-2 pr-5">
                  {docTypeIcon(
                    a.kind,
                    a.mime_type,
                    'h-4 w-4 mt-0.5 flex-shrink-0'
                  )}
                  <p className="text-xs font-semibold text-gray-900 leading-snug break-words">
                    {labelFromUrl(a.url)}
                  </p>
                </div>

                {/* Type */}
                {a.kind && (
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-700 capitalize">
                      {a.kind.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}

                {/* Date — document date preferred, scrape date as fallback */}
                {docDate(a) && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-700">
                      <span className="text-gray-900">
                        {docDate(a)!.label}:
                      </span>{' '}
                      {docDate(a)!.value}
                    </span>
                  </div>
                )}

                {/* Department / meeting type */}
                {(a.department || a.meeting_type) && (
                  <div className="pl-4 space-y-0.5">
                    {a.department && (
                      <p className="text-xs text-gray-700">
                        <span className="font-medium">Dept:</span>{' '}
                        {a.department}
                      </p>
                    )}
                    {a.meeting_type && (
                      <p className="text-xs text-gray-700">
                        <span className="font-medium">Meeting:</span>{' '}
                        {a.meeting_type}
                      </p>
                    )}
                  </div>
                )}

                {/* File size */}
                {a.byte_size != null && (
                  <p className="text-xs text-gray-900 pl-4">
                    {formatBytes(a.byte_size)}
                  </p>
                )}

                {/* MIME */}
                {a.mime_type && (
                  <p className="text-xs text-gray-900 pl-4">{a.mime_type}</p>
                )}

                {/* View archived document (PDF preview + download) */}
                <button
                  onClick={() => setViewerArtifact(a)}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                >
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  View document
                </button>
              </div>
            ))}

            {/* ── Uploaded session documents ── */}
            {sessionArtifacts.length > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide mt-4 mb-2">
                  Uploaded This Session
                </p>
                {sessionArtifacts.map((s) => (
                  <div
                    key={s.id}
                    className="relative rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1 shadow-sm"
                  >
                    <button
                      onClick={() =>
                        setSessionArtifacts((prev) =>
                          prev.filter((x) => x.id !== s.id)
                        )
                      }
                      className="absolute top-2 right-2 p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Remove uploaded document"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex items-start gap-2 pr-5">
                      <Paperclip className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs font-semibold text-gray-900 leading-snug break-words">
                        {s.filename}
                      </p>
                    </div>
                    <p className="text-xs text-amber-700 pl-6">
                      Session upload — indexed & searchable
                    </p>
                  </div>
                ))}
              </>
            )}

            {/* Upload in progress indicator */}
            {uploading && (
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-900">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400 flex-shrink-0" />
                Extracting and indexing document…
              </div>
            )}
          </div>
        </div>
      </div>

      {viewerArtifact && (
        <DocumentViewerModal
          artifactId={viewerArtifact.id}
          sourceUrl={viewerArtifact.url}
          title={labelFromUrl(viewerArtifact.url)}
          kind={viewerArtifact.kind}
          onClose={() => setViewerArtifact(null)}
        />
      )}
    </div>
  );
};
